import { useContext } from 'react';

import { useField } from 'react-final-form';
import { Link } from 'react-router-dom';

import {
  Button,
  Icons,
  SxProps,
  ExternalLink,
  Stack,
  tokens,
} from '@cast/design-system';

import { HpaSettingsBanner } from 'features/cluster/workload-optimization/_components/HpaSettingsBanner';
import { makeEditScalingPolicySettingsLink } from 'features/cluster/workload-optimization/navigation';
import { useDefaultScalingPolicyFormModelFormModel } from 'features/cluster/workload-optimization/scaling-policy-details/form/ScalingPolicyForm/_hooks/useDefaultScalingPolicyFormModel';

import { useHpaV2Status } from '../../_hooks/useHpaV2Status';
import { WorkloadDetailsContext } from '../_context';
import { useIsHpaSettingsMissingOnWorkloadForm } from '../_hooks/useIsHpaSettingsMissingOnWorkloadForm';
import {
  HorizontalRightsizingFormModelV2,
  HpaSettingsFormModel,
} from '../RightsizingSettings/_types';

import { getDocsLink } from '@cast/utils';

export const RightsizingHpaSettingsBanners = ({ sx }: { sx?: SxProps }) => {
  const { hpaV2Status } = useHpaV2Status();

  const { isHpaSettingsMissingOnWorkloadForm } =
    useIsHpaSettingsMissingOnWorkloadForm();
  const { workload } = useContext(WorkloadDetailsContext);
  const { id, isReadonly, isCastware } = workload?.verticalScalingPolicy || {};
  const { isNamespaceWritable } = useContext(WorkloadDetailsContext);
  const isNamespaceWritableForWorkload = isNamespaceWritable(
    workload?.namespace
  );

  const horizontalRightsizingField = useField<HorizontalRightsizingFormModelV2>(
    'horizontalRightsizing'
  );
  const { defaultScalingPolicyFormModel } =
    useDefaultScalingPolicyFormModelFormModel({ includeHpaSettings: true });

  const scalingPolicyHpaSettingsLink =
    id && !isReadonly && !isCastware
      ? makeEditScalingPolicySettingsLink({
          policyId: id,
        })
      : null;

  const handleCreateHpaObject = () => {
    if (!defaultScalingPolicyFormModel?.hpa) {
      return;
    }

    const { hpa } = defaultScalingPolicyFormModel;
    const currentHorizontalRightsizing = horizontalRightsizingField.input.value;

    // Map scaling policy HPA settings to workload HPA settings format
    // Remove takeOwnership as it's not part of workload HPA settings
    const hpaSettings: HpaSettingsFormModel = {
      basicBehavior: {
        minReplicas: hpa.basicBehavior.minReplicas,
        maxReplicas: hpa.basicBehavior.maxReplicas,
      },
      metrics: hpa.metrics,
      autoscalerBehavior: hpa.autoscalerBehavior,
    };

    // Update the form with HPA settings and set isOverridden to true
    horizontalRightsizingField.input.onChange({
      ...currentHorizontalRightsizing,
      hpaSettings,
      isOverridden: true,
    });
  };

  if (
    isCastware ||
    !isHpaSettingsMissingOnWorkloadForm ||
    hpaV2Status !== 'available'
  ) {
    return null;
  }

  return (
    <HpaSettingsBanner
      testId="hpa-settings-missing-banner"
      hexIcon={{
        Icon: <Icons.Question weight="fill" />,
        size: 50,
        iconColor: tokens.theme.fg.status.onNeutralSubtle,
        hexColor: tokens.theme.bg.status.neutralSubtle,
      }}
      title="Missing HPA configuration"
      sx={sx}
      description={
        isNamespaceWritableForWorkload ? (
          <>
            Configure horizontal optimization for this workload in the policy or
            create a workload-level HPA object.{' '}
            <ExternalLink link={getDocsLink('docs_woop_hpa_v2')} noIcon>
              Documentation
            </ExternalLink>
          </>
        ) : (
          'You are not allowed to create a HPA object in this namespace'
        )
      }
      actionButtons={
        isNamespaceWritableForWorkload ? (
          <Stack direction="row" gap={12}>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCreateHpaObject}
              testId="hpa-banner-create-hpa-object-button"
            >
              Create HPA object
            </Button>
            {scalingPolicyHpaSettingsLink && (
              <Button
                size="sm"
                component={Link}
                to={scalingPolicyHpaSettingsLink}
                testId="hpa-banner-configure-in-policy-button"
              >
                Configure in policy
              </Button>
            )}
          </Stack>
        ) : undefined
      }
    />
  );
};
